/* oxlint-disable react/jsx-props-no-spreading -- props are intentionally forwarded by this adapter */
import {
  FloatingPortal,
  autoUpdate,
  offset,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react'
import { EllipsisVerticalIcon } from '@heroicons/react/20/solid'
import classNames from 'classnames'
import {
  EditPencil as EditPencilIcon,
  Pin as PinIcon,
  Settings as SettingsIcon,
  Trash as TrashIcon,
} from 'iconoir-react'
import { observer } from 'mobx-react-lite'
import { useRef, useState } from 'react'
import { Tooltip } from '../Tooltip'
import { CountBadge } from 'app/components/inbox/CountBadge'
import { useIsOrgUser } from 'app/hooks/useIsOrgUser'
import type { List } from 'app/generated/graphql'
import type { CustomPageJson, CustomPages } from 'app/types/entities'
import type { PipelineType } from 'app/types/entities'
import { useUser } from 'app/utils'

export type ChipProps = {
  customPage?: CustomPageJson
  total: number
  className?: string
  isDefault?: boolean
  /** The homepage nobody chose: shown as pinned, but there is no pin to take back. */
  lockedDefault?: boolean
  isActive?: boolean
  pipeline?: PipelineType
  /** A contact list built from the List API: with an `onEditList`, its rule can be edited. */
  list?: List
  href?: string
  countColor?: string
  onEditList?: () => void
  /** A list that owns a settings dialog rather than a filter editor: manual lists. */
  onSettings?: () => void
  onRemovePipeline?: () => void
  onSetAsDefault: () => void
  hide?: boolean
}

// Shared ref to close the previously opened chip menu
let closeActiveMenu: (() => void) | null = null
let activeMenuId: object | null = null

// ============================================================================
// Hook: Business Logic
// ============================================================================

export function useChipV2({
  customPage,
  list,
  pipeline,
  href,
  onEditList,
  onSettings,
  onRemovePipeline,
  onSetAsDefault,
}: Pick<
  ChipProps,
  | 'customPage'
  | 'list'
  | 'pipeline'
  | 'href'
  | 'isDefault'
  | 'onEditList'
  | 'onSettings'
  | 'onRemovePipeline'
  | 'onSetAsDefault'
>) {
  const user = useUser()
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const menuId = useRef({})

  const setIsOpenExclusive = (open: boolean | ((prev: boolean) => boolean)) => {
    setIsOpen((prev) => {
      const next = typeof open === `function` ? open(prev) : open
      if (next) {
        if (activeMenuId !== menuId.current) {
          closeActiveMenu?.()
        }
        closeActiveMenu = () => setIsOpen(false)
        activeMenuId = menuId.current
      } else if (activeMenuId === menuId.current) {
        closeActiveMenu = null
        activeMenuId = null
      }
      return next
    })
  }
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null)
  const [referenceElement, setReferenceElement] = useState<HTMLDivElement | null>(null)
  const isOrgUser = useIsOrgUser()

  // The pin toggles, so the pinned row keeps it: it is the only control that can clear
  // the homepage. Gating this on `isDefault` hid the control from exactly the row that
  // needed it, leaving a homepage that could be set and never unset.
  const canPin = (() => {
    if (href?.includes(`/settings`)) return false
    // Org users cannot pin when in a workspace
    if (isOrgUser && user?.workspaceId) return false
    return true
  })()

  const canSettings = !!onSettings
  const canEditList = !!((customPage || list) && onEditList)

  const floatingData = useFloating({
    open: isOpen,
    placement: `right-start`,
    elements: { reference: referenceElement, floating: popperElement },
    onOpenChange: canPin && (customPage || pipeline || canSettings || canEditList) ? setIsOpenExclusive : undefined,
    whileElementsMounted: autoUpdate,
    middleware: [offset({ mainAxis: 16, crossAxis: -12 })],
  })

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(floatingData.context),
    useDismiss(floatingData.context),
  ])

  const handleEditList = (evt: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    evt.preventDefault()
    evt.stopPropagation()
    onEditList?.()
    setIsOpenExclusive(false)
  }

  const handleSettings = (evt: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    evt.preventDefault()
    evt.stopPropagation()
    onSettings?.()
    setIsOpenExclusive(false)
  }

  const handleSetAsDefault = (evt: React.MouseEvent<HTMLDivElement | SVGSVGElement, MouseEvent>) => {
    evt.preventDefault()
    evt.stopPropagation()
    onSetAsDefault()
    setIsOpenExclusive(false)
  }

  const handleRemovePipeline = (evt: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    evt.preventDefault()
    evt.stopPropagation()
    onRemovePipeline?.()
    setIsOpenExclusive(false)
  }

  // A caller omits the handler to say the action is unavailable for this item (e.g. a
  // system pipeline, whose deletePipeline mutation the server always rejects). Gate the
  // menu entry itself on that, not only the click handler, or the entry still renders and
  // does nothing when clicked.
  const canRemovePipeline = !!(pipeline && onRemovePipeline)

  const showMenuButton = canEditList || canSettings || canRemovePipeline

  const showCountChip: boolean = (() => {
    //Create regex patterns dynamically from CustomPages type
    const customPagePatterns: Record<CustomPages | `companies`, RegExp> = {
      companies: /^\/companies(?:\/.*)?(?:\?.*)?$/,
      contacts: /^\/contacts(?:\/.*)?(?:\?.*)?$/,
      activities: /^\/activities(?:\/.*)?(?:\?.*)?$/,
      tasks: /^\/tasks(?:\/.*)?(?:\?.*)?$/,
      treatmentPlans: /^\/treatment-plans(?:\/.*)?(?:\?.*)?$/,
      appointments: /^\/appointments(?:\/|\?|$)/,
    }
    for (const [, pattern] of Object.entries(customPagePatterns)) {
      if (href?.match(pattern)) {
        return true
      }
    }
    // Add pipelines pattern
    if (href?.match(/^\/pipelines(?:\/.*)?(?:\?.*)?$/)) {
      return true
    }
    // Add inbox pattern
    if (href?.match(/^\/inbox(?:\/.*)?(?:\?.*)?$/)) {
      return true
    }
    // Add system lists pattern
    if (href?.match(/^\/lists\/[^/?]+(?:\?.*)?$/)) {
      return true
    }
    if (href?.match(/^\/treatment-plans\/?(?:\?.*)?$/)) {
      return true
    }
    return false
  })()

  // Only stop propagation if there are interactive elements in the chip
  const hasInteractiveContent = !!(customPage || pipeline || canPin || canSettings || canEditList)

  return {
    isOpen,
    setIsOpen: setIsOpenExclusive,
    setReferenceElement,
    setPopperElement,
    floatingStyles: floatingData.floatingStyles,
    getReferenceProps,
    getFloatingProps,
    canPin,
    showCountChip,
    hasInteractiveContent,
    canEditList,
    canSettings,
    canRemovePipeline,
    showMenuButton,
    handleEditList,
    handleSettings,
    handleSetAsDefault,
    handleRemovePipeline,
  }
}

// ============================================================================
// View: Pure Presentation Component
// ============================================================================

export type ChipV2ViewProps = {
  // Display props from ChipProps
  total: number
  className?: string
  isDefault?: boolean
  lockedDefault?: boolean
  isActive?: boolean
  pipeline?: PipelineType
  href?: string
  countColor?: string
  hide?: boolean
  // Computed values from hook
  isOpen: boolean
  canPin: boolean
  showCountChip: boolean
  hasInteractiveContent: boolean
  canEditList: boolean
  canSettings: boolean
  canRemovePipeline: boolean
  showMenuButton: boolean
  floatingStyles: React.CSSProperties
  setReferenceElement: React.Dispatch<React.SetStateAction<HTMLDivElement | null>>
  setPopperElement: React.Dispatch<React.SetStateAction<HTMLDivElement | null>>
  getReferenceProps: (props?: object) => object
  getFloatingProps: (props?: object) => object
  handleEditList: (evt: React.MouseEvent<HTMLDivElement>) => void
  handleSettings: (evt: React.MouseEvent<HTMLDivElement>) => void
  handleSetAsDefault: (evt: React.MouseEvent<HTMLDivElement | SVGSVGElement>) => void
  handleRemovePipeline: (evt: React.MouseEvent<HTMLDivElement>) => void
  onToggleOpen: () => void
  onMenuMouseLeave: () => void
}

export function ChipV2View({
  total,
  className,
  isDefault = false,
  lockedDefault = false,
  isActive = false,
  hide = false,
  countColor,
  isOpen,
  canPin,
  showCountChip,
  hasInteractiveContent,
  canEditList,
  canSettings,
  canRemovePipeline,
  showMenuButton,
  floatingStyles,
  setReferenceElement,
  setPopperElement,
  getReferenceProps,
  getFloatingProps,
  handleEditList,
  handleSettings,
  handleSetAsDefault,
  handleRemovePipeline,
  onToggleOpen,
  onMenuMouseLeave,
}: ChipV2ViewProps) {
  return (
    <div
      className="-mr-px flex shrink-0 items-center gap-x-1"
      ref={setReferenceElement}
      onClick={
        hasInteractiveContent
          ? (evt) => {
              evt.preventDefault()
              evt.stopPropagation()
            }
          : undefined
      }
      {...getReferenceProps()}
    >
      <>
        {countColor?.includes(`bg-`) && total > 0 ? (
          <CountBadge value={total} className={classNames(hide ? `hidden` : ``)} isActive={isActive} />
        ) : (
          <span
            className={classNames(
              className,
              `flex h-6 w-8 items-center justify-end rounded-lg pr-1 text-label-xs`,
              hide ? `hidden` : ``,
              countColor ? countColor : isActive ? `text-strong` : showCountChip ? `text-subtle` : ``,
            )}
          >
            {showCountChip && <>{total}</>}
          </span>
        )}
        {showMenuButton ? (
          <div
            data-testid="nav-row-menu"
            className={
              isOpen
                ? `flex h-6 w-6 cursor-pointer items-center justify-center rounded-lg bg-surface`
                : `hidden h-6 w-0 cursor-pointer items-center justify-center rounded-lg bg-surface group-hover/nav-item:flex group-hover/nav-item:w-6`
            }
            onClick={(evt) => {
              evt.preventDefault()
              evt.stopPropagation()
              onToggleOpen()
            }}
          >
            <EllipsisVerticalIcon height={16} width={16} className="text-default" />
          </div>
        ) : canPin ? (
          // The pin is a hover-only control on every row and is filled on the homepage row,
          // the only place the pinned state shows. The default homepage has no pin to take
          // back, so the icon drops its button styling and the tooltip says how to move it.
          <Tooltip
            message={
              lockedDefault
                ? `Your homepage by default. Pin another page to change it.`
                : isDefault
                  ? `Remove homepage`
                  : `Set as homepage`
            }
            className="hidden h-6 w-0 group-hover/nav-item:flex group-hover/nav-item:w-6"
          >
            <div
              className={classNames(
                `flex aspect-square h-6 w-6 items-center justify-center rounded-lg`,
                lockedDefault ? `cursor-default` : `cursor-pointer bg-surface`,
              )}
              onClick={lockedDefault ? undefined : handleSetAsDefault}
            >
              <PinIcon width={16} height={16} className="text-amber-500" fill={isDefault ? `currentColor` : `none`} />
            </div>
          </Tooltip>
        ) : null}
      </>

      {isOpen && (
        <FloatingPortal id="nav-item-menu">
          <div
            ref={setPopperElement}
            className="z-50 flex w-44 flex-col rounded-md border border-default bg-surface-raised px-1 py-1 shadow-elevation-5"
            style={floatingStyles}
            onMouseLeave={onMenuMouseLeave}
            {...getFloatingProps()}
          >
            {canEditList && (
              <div
                role="button"
                className="flex h-8 cursor-pointer items-center gap-2 rounded-md px-1 text-body-sm text-default hover:bg-(--menu-hover)"
                tabIndex={0}
                onClick={handleEditList}
              >
                <EditPencilIcon className="h-4 w-4" />
                <span>Edit List</span>
              </div>
            )}
            {canSettings && (
              <div
                role="button"
                className="flex h-8 cursor-pointer items-center gap-2 rounded-md px-1 text-body-sm text-default hover:bg-(--menu-hover)"
                tabIndex={0}
                onClick={handleSettings}
              >
                <SettingsIcon className="h-4 w-4" />
                <span>Settings</span>
              </div>
            )}
            {canRemovePipeline && (
              <div
                role="button"
                className="flex h-8 cursor-pointer items-center gap-2 rounded-md px-1 text-body-sm text-default hover:bg-(--menu-hover)"
                tabIndex={0}
                onClick={handleRemovePipeline}
              >
                <TrashIcon className="h-4 w-4" />
                <span>Remove Pipeline</span>
              </div>
            )}
            {canPin && !lockedDefault ? (
              <div
                role="button"
                tabIndex={0}
                onClick={handleSetAsDefault}
                className="flex h-8 items-center gap-2 rounded-md px-1 text-body-sm text-default hover:bg-(--menu-hover)"
              >
                <PinIcon className="h-4 w-4 text-amber-500" fill={isDefault ? `currentColor` : `none`} />
                <span>{isDefault ? `Remove homepage` : `Set as homepage`}</span>
              </div>
            ) : null}
          </div>
        </FloatingPortal>
      )}
    </div>
  )
}

// ============================================================================
// Default Export: Composition
// ============================================================================

// An observer because `canPin` reads the auth store and a feature toggle, both of which
// arrive after the first render. Without it the pin was computed once against a user and
// a toggle that were not loaded yet, and never recomputed, so whether the control existed
// at all came down to load timing.
export default observer(function ChipV2(props: ChipProps) {
  const { setIsOpen, ...logic } = useChipV2(props)
  const { onEditList, onSettings, onRemovePipeline, onSetAsDefault, list, ...displayProps } = props
  return (
    <ChipV2View
      {...displayProps}
      {...logic}
      onToggleOpen={() => setIsOpen((prev) => !prev)}
      onMenuMouseLeave={() => setIsOpen(false)}
    />
  )
})
